/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import { forwardRef, lazy, Suspense } from 'react';

import { css, jsx } from '@compiled/react';

import { isExperimentEnabled } from '@atlaskit/platform-feature-experiments/is-experiment-enabled';
import { Popup } from '@atlaskit/popup/popup';
import type { PopupComponentProps } from '@atlaskit/popup/types';

import { type TypeDefinitionPopupContentProps } from './definition-popup-content';

const DEFINITION_DIALOG_TITLE_ID = 'definition-dialog-title';

const popupContainerStyles = css({
	zIndex: 300,
});
const CustomPopupContainer = forwardRef<HTMLDivElement, PopupComponentProps>(
	({ children, ...props }, ref) => {
		if (isExperimentEnabled('a11y-fixes-week1-june-2026')) {
			return (
				<div
					{...props}
					ref={ref}
					css={popupContainerStyles}
					role="dialog"
					aria-modal="false"
					aria-labelledby={DEFINITION_DIALOG_TITLE_ID}
				>
					{children}
				</div>
			);
		}
		return (
			<span {...props} ref={ref} css={popupContainerStyles}>
				{children}
			</span>
		);
	},
);

const definitionPopupTriggerStylesStatic = css({
	opacity: 0,
	position: 'absolute',
	pointerEvents: 'none',
	backgroundColor: 'transparent',
	left: '0px',
	bottom: '0px',
});

const LazyDefinitionPopupContent = lazy(() =>
	import(
		/* webpackChunkName: '@atlaskit-internal_kd-definition-popup-content' */ './definition-popup-content'
	).then((module) => ({
		default: module.DefinitionPopupContent,
	})),
);

export function DefinitionPopup({
	onClose,
	triggerPosition,
	onLoad,
	...definitionPopupContentProps
}: {
	onClose: () => void;
	triggerPosition: {
		height: number;
		left: number;
		top: number;
		width: number;
	};
} & TypeDefinitionPopupContentProps): JSX.Element {
	const definitionPopupTriggerStylesDynamic = {
		height: `${triggerPosition.height}px`,
		left: `${triggerPosition.left}px`,
		top: `${triggerPosition.top}px`,
		width: `${triggerPosition.width}px`,
	};

	return (
		<Popup
			content={({ update }) => (
				<Suspense fallback={null}>
					<LazyDefinitionPopupContent
						{...definitionPopupContentProps}
						onClose={onClose}
						onLoad={() => {
							onLoad?.();
							update();
						}}
					/>
				</Suspense>
			)}
			onClose={onClose}
			placement="bottom-start"
			popupComponent={CustomPopupContainer}
			testId="definition-popup"
			trigger={(triggerProps) => (
				<span
					{...triggerProps}
					css={definitionPopupTriggerStylesStatic}
					style={definitionPopupTriggerStylesDynamic}
					data-testid="definition-popup-trigger"
				/>
			)}
			isOpen
		/>
	);
}
